import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  Person,
  Email,
  CalendarToday,
  Security,
  Save,
  Cancel,
  Edit,
  PhotoCamera,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchProfile, updateProfile, clearError } from '../store/slices/usersSlice';
import { fetchUser } from '../store/slices/authSlice';
import { addNotification } from '../store/slices/notificationSlice';
import { safeFormatDate } from '../utils/dateUtils';
import axios from 'axios';
import backendUrl from '../store/config';
import { useSidebar } from '../contexts/SidebarContext';

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { sidebarOpen } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { profile, loading, error } = useAppSelector((state: any) => state.users);
  const token = useAppSelector((state: any) => state.auth.token);

  const [isEditing, setIsEditing] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    preferences: {
      email_notifications: true,
      theme: 'light' as 'light' | 'dark',
      language: 'en',
    },
  });

  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username,
        email: profile.email,
        preferences: {
          email_notifications: profile.preferences?.email_notifications ?? true,
          theme: profile.preferences?.theme ?? 'light',
          language: profile.preferences?.language ?? 'en',
        },
      });
    }
  }, [profile]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleSave = async () => {
    try {
      const result = await dispatch(updateProfile(formData)).unwrap();
      
      // Refetch profile to get updated data
      await dispatch(fetchProfile());
      
      // Show success notification
      dispatch(addNotification({
        type: 'success',
        title: 'Profile Updated',
        message: result.message || 'Your profile has been updated successfully.',
        duration: 5000,
        userId: profile?.id
      }));
      
      setIsEditing(false);
    } catch (error: any) {
      // Show error notification
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      dispatch(addNotification({
        type: 'error',
        title: 'Update Failed',
        message: errorMessage,
        duration: 7000,
        userId: profile?.id
      }));
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        username: profile.username,
        email: profile.email,
        preferences: {
          email_notifications: profile.preferences?.email_notifications ?? true,
          theme: profile.preferences?.theme ?? 'light',
          language: profile.preferences?.language ?? 'en',
        },
      });
    }
    setIsEditing(false);
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('File size must be less than 5MB');
      return;
    }

    setAvatarUploading(true);
    setAvatarError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${backendUrl}/api/users/upload-avatar`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refresh profile to get updated avatar
      dispatch(fetchProfile());
      // Also refresh auth state so Navbar shows updated avatar
      dispatch(fetchUser());
    } catch (err: any) {
      setAvatarError(err.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
    }
  };

  const getAvatarSrc = () => {
    if (profile?.avatar) {
      return `${backendUrl}${profile.avatar}`;
    }
    return undefined;
  };

  const handlePasswordChange = async () => {
    setPasswordChangeError(null);
    setPasswordChangeSuccess(null);

    // Validation
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      const errorMsg = 'All password fields are required';
      setPasswordChangeError(errorMsg);
      dispatch(addNotification({
        type: 'error',
        title: 'Validation Error',
        message: errorMsg,
        duration: 5000,
        userId: profile?.id
      }));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      const errorMsg = 'New passwords do not match';
      setPasswordChangeError(errorMsg);
      dispatch(addNotification({
        type: 'error',
        title: 'Validation Error',
        message: errorMsg,
        duration: 5000,
        userId: profile?.id
      }));
      return;
    }

    if (passwordData.newPassword.length < 8) {
      const errorMsg = 'New password must be at least 8 characters long';
      setPasswordChangeError(errorMsg);
      dispatch(addNotification({
        type: 'error',
        title: 'Validation Error',
        message: errorMsg,
        duration: 5000,
        userId: profile?.id
      }));
      return;
    }

    setPasswordChangeLoading(true);

    try {
      const response = await axios.put(
        `${backendUrl}/api/users/change-password`,
        {
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const successMsg = response.data?.message || 'Password changed successfully!';
      setPasswordChangeSuccess(successMsg);
      
      // Show success notification
      dispatch(addNotification({
        type: 'success',
        title: 'Password Changed',
        message: successMsg,
        duration: 5000,
        userId: profile?.id
      }));
      
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setIsChangingPassword(false);
    } catch (err: any) {
      let errorMessage = 'Failed to change password';
      
      if (err.response?.data) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          // Handle validation errors array
          errorMessage = err.response.data.detail.map((error: any) => error.msg || error).join(', ');
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      
      setPasswordChangeError(errorMessage);
      
      // Show error notification
      dispatch(addNotification({
        type: 'error',
        title: 'Password Change Failed',
        message: errorMessage,
        duration: 7000,
        userId: profile?.id
      }));
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordChangeError(null);
    setPasswordChangeSuccess(null);
    setIsChangingPassword(false);
  };

  const togglePasswordVisibility = (field: 'old' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Failed to load profile</Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        marginLeft: sidebarOpen ? '0px' : '0px',
        transition: (theme) =>
          theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        width: sidebarOpen ? 'calc(100% - 0px)' : 'calc(100% - 0px)',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="md" sx={{ 
        mt: isMobile ? 1 : 2, 
        mb: isMobile ? 1 : 2, 
        px: isMobile ? 1 : 2,
        height: '100%',
        overflowY: 'auto',
        // Custom scrollbar styling - positioned at right edge
        '&::-webkit-scrollbar': {
          width: '12px',
        },
        '&::-webkit-scrollbar-track': {
          background: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f1f3f4',
          borderRadius: '6px',
          margin: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: (theme) => theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
          borderRadius: '6px',
          border: '2px solid transparent',
          backgroundClip: 'content-box',
          '&:hover': {
            background: (theme) => theme.palette.mode === 'dark' ? '#777' : '#a8a8a8',
            backgroundClip: 'content-box',
          },
        },
        // Firefox scrollbar styling
        scrollbarWidth: 'thin',
        scrollbarColor: (theme) => theme.palette.mode === 'dark' ? '#555 #2a2a2a' : '#c1c1c1 #f1f3f4',
      }}>
      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, mb: isMobile ? 1 : 2 }}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            component="h1" 
            sx={{ 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: isMobile ? '1.25rem' : undefined
            }}
          >
            Profile Settings
          </Typography>
        </Box>
      </Fade>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 1 : 2}>
        {/* Main Profile & Preferences Card */}
        <Grid item xs={12} md={8} order={isMobile ? 2 : 1}>
          <Fade in timeout={600}>
            <Card>
            <CardContent sx={{ textAlign: isMobile ? 'center' : 'left' }}>
              {/* Header with Edit/Save/Cancel */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                mb: isMobile ? 2 : 3,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 0
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: isMobile ? '1.1rem' : undefined,
                    mb: isMobile ? 1 : 0
                  }}
                >
                  Personal Information
                </Typography>
                {isEditing ? (
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1,
                    flexDirection: isMobile ? 'column' : 'row',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    <Button 
                      variant="outlined" 
                      startIcon={<Cancel />} 
                      onClick={handleCancel}
                      size={isMobile ? 'small' : 'medium'}
                      fullWidth={isMobile}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      startIcon={<Save />} 
                      onClick={handleSave}
                      size={isMobile ? 'small' : 'medium'}
                      fullWidth={isMobile}
                    >
                      Save Changes
                    </Button>
                  </Box>
                ) : (
                  <Button 
                    variant="outlined" 
                    startIcon={<Edit />} 
                    onClick={() => setIsEditing(true)}
                    size={isMobile ? 'small' : 'medium'}
                    fullWidth={isMobile}
                  >
                    Edit
                  </Button>
                )}
              </Box>

              {/* Personal Info Fields */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Preferences
              </Typography>
              {/* Preferences Fields */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.preferences.email_notifications}
                        onChange={(e) => handleInputChange('preferences.email_notifications', e.target.checked)}
                        disabled={!isEditing}
                      />
                    }
                    label="Email Notifications"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    Receive notifications about new summaries and updates
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Theme"
                    value={formData.preferences.theme}
                    onChange={(e) => handleInputChange('preferences.theme', e.target.value)}
                    disabled={!isEditing}
                  >
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Language"
                    value={formData.preferences.language}
                    onChange={(e) => handleInputChange('preferences.language', e.target.value)}
                    disabled={!isEditing}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                Security
              </Typography>
              
              {/* Password Change Section */}
              <Box sx={{ mb: 2 }}>
                {passwordChangeSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {passwordChangeSuccess}
                  </Alert>
                )}
                
                {passwordChangeError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {passwordChangeError}
                  </Alert>
                )}
                
                {isChangingPassword ? (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type={showPasswords.old ? 'text' : 'password'}
                          label="Current Password"
                          value={passwordData.oldPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, oldPassword: e.target.value }))}
                          InputProps={{
                            startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                            endAdornment: (
                              <IconButton
                                onClick={() => togglePasswordVisibility('old')}
                                edge="end"
                              >
                                {showPasswords.old ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type={showPasswords.new ? 'text' : 'password'}
                          label="New Password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          InputProps={{
                            startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                            endAdornment: (
                              <IconButton
                                onClick={() => togglePasswordVisibility('new')}
                                edge="end"
                              >
                                {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type={showPasswords.confirm ? 'text' : 'password'}
                          label="Confirm New Password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          InputProps={{
                            startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                            endAdornment: (
                              <IconButton
                                onClick={() => togglePasswordVisibility('confirm')}
                                edge="end"
                              >
                                {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      mt: 2,
                      flexDirection: isMobile ? 'column' : 'row'
                    }}>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handlePasswordCancel}
                        disabled={passwordChangeLoading}
                        size={isMobile ? 'small' : 'medium'}
                        fullWidth={isMobile}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handlePasswordChange}
                        disabled={passwordChangeLoading}
                        size={isMobile ? 'small' : 'medium'}
                        fullWidth={isMobile}
                      >
                        {passwordChangeLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          'Change Password'
                        )}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Lock />}
                    onClick={() => setIsChangingPassword(true)}
                    disabled={isEditing}
                    size={isMobile ? 'small' : 'medium'}
                    fullWidth={isMobile}
                  >
                    Change Password
                  </Button>
                )}
              </Box>
            </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Profile Summary Card */}
        <Grid item xs={12} md={4} order={isMobile ? 1 : 2}>
          <Fade in timeout={600}>
            <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: isMobile ? 1 : 2 }}>
                <Avatar
                  src={getAvatarSrc()}
                  sx={{
                    width: isMobile ? 60 : 80,
                    height: isMobile ? 60 : 80,
                    bgcolor: 'primary.main',
                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                  }}
                >
                  {profile.username ? profile.username.charAt(0).toUpperCase() : '?'}
                </Avatar>
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                  component="label"
                  disabled={avatarUploading}
                >
                  {avatarUploading ? (
                    <CircularProgress size={isMobile ? 15 : 20} color="inherit" />
                  ) : (
                    <PhotoCamera sx={{ fontSize: isMobile ? 15 : 20 }} />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleAvatarUpload}
                  />
                </IconButton>
              </Box>

              {avatarError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {avatarError}
                </Alert>
              )}

              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                {profile.username || 'Unknown User'}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {profile.email || 'No email'}
              </Typography>

              <Chip
                label={profile.role || 'user'}
                color={(profile.role || 'user') === 'admin' ? 'primary' : 'default'}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarToday sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Member since {safeFormatDate(profile.created_at, 'MMM yyyy', 'Unknown')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Security sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {profile.is_active ? 'Active Account' : 'Inactive Account'}
                  </Typography>
                </Box>


              </Box>
            </CardContent>
          </Card>

          </Fade>

          <Fade in timeout={600}>
            {/* Account Status Card */}
            <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Account Status
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Status</Typography>
                <Chip
                  label={profile.is_active ? 'Active' : 'Inactive'}
                  color={profile.is_active ? 'success' : 'error'}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Role</Typography>
                <Chip
                  label={profile.role || 'user'}
                  color={(profile.role || 'user') === 'admin' ? 'primary' : 'default'}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Email Verified</Typography>
                <Chip
                  label="Verified"
                  color="success"
                  size="small"
                />
              </Box>
            </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
      </Container>
    </Box>
  );
};

export default Profile;
